import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Collapse, Text } from '@mantine/core';
import { IconRobot, IconChevronDown, IconCheck, IconFileText, IconAlertTriangle } from '@tabler/icons-react';
import { CitationList, MessageMarkdown, type Citation } from '../MessageMarkdown';
import { safelyOpenUrl } from '../MessageMarkdown/utils/urlValidation';
import { truncateSourceChunksInMarkdownForDisplay } from '../../utils/sourceContentTruncation';
import {
  getAgentChunkAccordionSectionKey,
  useAgentChunkAccordionStore,
} from '../../../../stores/agentChunkAccordionStore';
import type { Reference } from '../../types/conversation';
import classes from './AgentChunkAccordion.module.css';

interface AgentChunkAccordionCall {
  id?: string;
  agent: string;
  tool?: string;
  args?: Record<string, unknown>;
  content: string;
  status?: 'running' | 'done' | 'failed';
  error?: string;
}

/** Maps backend agent names (user_facing_name) to human-readable action descriptions. */
const AGENT_DISPLAY_LABELS: Record<string, string> = {
  'File Agent': 'Analysing Files',
  'PPL Agent': 'Searching Policy Documents',
  'Health & Safety Agent': 'Searching H&S Documents',
  'FarmSource Agent': 'Searching FarmSource Documents',
  'NZDC Agent': 'Searching NZDC Documents',
  'NZM Agent': 'Searching NZM Documents',
  'Asset Management Agent': 'Searching Asset Documents',
  'WebSearch Agent': 'Searching the Web',
  'Genie Agent': 'Querying Live Data',
  'Image Agent': 'Generating Image',
  'Code Interpreter Agent': 'Running Code',
  'Functional Spec Finder': 'Finding Functional Specs',
};

interface AgentChunkAccordionProps {
  chunks?: Record<string, string>;
  toolCalls?: AgentChunkAccordionCall[];
  dataPoints?: Reference[];
  isStreaming?: boolean;
  collapseByDefault?: boolean;
  className?: string;
  scopeId?: string;
}

interface AgentDisplayItem {
  key: string;
  label: string;
  content: string;
  status?: 'running' | 'done' | 'failed';
  error?: string;
}

const EMPTY_COLLAPSED_AGENT_KEYS: string[] = [];
const EMPTY_EXPANDED_SOURCE_KEYS: string[] = [];
const RETRIEVED_DOCUMENTS_HEADING_REGEX = /^#{1,6}\s*Retrieved Documents\s*$/im;
const SOURCE_LINK_REGEX = /\[Source\]\([^)]+\)/i;

const splitRetrievedDocuments = (content: string) => {
  const headingMatch = RETRIEVED_DOCUMENTS_HEADING_REGEX.exec(content);
  if (!headingMatch) {
    return null;
  }

  const sourceContent = content.slice(headingMatch.index + headingMatch[0].length).trim();
  if (!sourceContent || !SOURCE_LINK_REGEX.test(sourceContent)) {
    return null;
  }

  return {
    leadContent: content.slice(0, headingMatch.index).trim(),
    sourceContent,
  };
};

const AgentChunkContent: React.FC<{ content: string; isStreaming?: boolean }> = ({ content, isStreaming = false }) => {
  const [citations, setCitations] = useState<Citation[]>([]);
  const displayContent = useMemo(
    () => isStreaming ? content : truncateSourceChunksInMarkdownForDisplay(content),
    [content, isStreaming],
  );

  const handleCitationClick = useCallback((citation: Citation) => {
    if (citation.url) {
      safelyOpenUrl(citation.url);
    }
  }, []);

  // While streaming, still render markdown, but skip citation extraction/list
  // bookkeeping so each WebSocket chunk can paint immediately without building
  // a client-side backlog.
  if (isStreaming) {
    return <MessageMarkdown content={displayContent} skipEnhancements />;
  }

  return (
    <>
      <MessageMarkdown
        content={displayContent}
        onCitationClick={handleCitationClick}
        onCitationsExtracted={setCitations}
      />
      {citations.length > 0 && (
        <Box mt="xs">
          <CitationList
            citations={citations}
            onCitationClick={handleCitationClick}
          />
        </Box>
      )}
    </>
  );
};

export const AgentChunkAccordion: React.FC<AgentChunkAccordionProps> = ({
  chunks,
  toolCalls,
  dataPoints,
  isStreaming = false,
  collapseByDefault = false,
  className,
  scopeId: providedScopeId,
}) => {
  const agents = useMemo<AgentDisplayItem[]>(() => {
    if (toolCalls?.length) {
      const agentCounts = new Map<string, number>();
      const totalByAgent = new Map<string, number>();

      for (const call of toolCalls) {
        const name = call.agent.trim();
        if (name) totalByAgent.set(name, (totalByAgent.get(name) || 0) + 1);
      }

      return toolCalls
        .filter((call) => call.agent.trim() && (call.content.trim() || call.status === 'running' || call.status === 'failed'))
        .map((call, idx) => {
          const name = call.agent.trim();
          const displayLabel = AGENT_DISPLAY_LABELS[name];
          const count = (agentCounts.get(name) || 0) + 1;
          agentCounts.set(name, count);
          const isMultiple = (totalByAgent.get(name) || 0) > 1;

          let suffix = '';
          if (isMultiple) {
            // Prefer the sub-query as a meaningful disambiguator over a bare number
            const query = call.args?.query;
            if (typeof query === 'string' && query.trim()) {
              const truncated = query.length > 45 ? `${query.slice(0, 45)}\u2026` : query;
              suffix = ` \u00b7 ${truncated}`;
            } else {
              suffix = ` #${count}`;
            }
          } else if (!displayLabel && call.tool) {
            // For agents without a display label, keep the raw tool name for context
            suffix = ` \u00b7 ${call.tool}`;
          }

          return {
            key: getAgentChunkAccordionSectionKey(call, idx),
            label: `${displayLabel ?? name}${suffix}`,
            content: call.content,
            status: call.status,
            error: call.error,
          };
        });
    }

    return Object.entries(chunks || {})
      .filter(([name, content]) => name.trim() && content.trim())
      .map(([name, content]) => ({ key: name, label: name, content, status: 'done' as const }));
  }, [chunks, toolCalls]);

  const agentKeysSignature = useMemo(
    () => agents.map((agent) => agent.key).join('\u001f'),
    [agents],
  );
  const scopeId = useMemo(
    () => providedScopeId || `agent-chunk:${agentKeysSignature}`,
    [agentKeysSignature, providedScopeId],
  );
  const collapsedAgentKeys = useAgentChunkAccordionStore(
    (state) => state.collapsedByScope[scopeId] ?? EMPTY_COLLAPSED_AGENT_KEYS,
  );
  const hasInitializedCollapsedScope = useAgentChunkAccordionStore(
    (state) => state.initializedCollapsedScopes[scopeId] === true,
  );
  const expandedSourceKeys = useAgentChunkAccordionStore(
    (state) => state.expandedSourcesByScope?.[scopeId] ?? EMPTY_EXPANDED_SOURCE_KEYS,
  );
  const toggleSection = useAgentChunkAccordionStore((state) => state.toggleSection);
  const toggleSources = useAgentChunkAccordionStore((state) => state.toggleSources);
  const initializeCollapsedSections = useAgentChunkAccordionStore(
    (state) => state.initializeCollapsedSections,
  );
  const collapsedAgents = useMemo(() => new Set(collapsedAgentKeys), [collapsedAgentKeys]);
  const defaultCollapsedAgents = useMemo(
    () => new Set(agents.map((agent) => agent.key)),
    [agents],
  );
  const effectiveCollapsedAgents =
    collapseByDefault && !hasInitializedCollapsedScope ? defaultCollapsedAgents : collapsedAgents;
  const expandedSources = useMemo(() => new Set(expandedSourceKeys), [expandedSourceKeys]);

  useEffect(() => {
    if (collapseByDefault && agents.length > 0) {
      initializeCollapsedSections(scopeId, agents.map((agent) => agent.key));
    }
  }, [agents, collapseByDefault, initializeCollapsedSections, scopeId]);

  const toggle = (agent: string, currentlyExpanded: boolean) => {
    toggleSection(scopeId, agent, currentlyExpanded);
  };

  const sourceDocumentsFromDataPoints = useMemo(() => {
    if (!dataPoints || dataPoints.length === 0) {
      return '';
    }

    return dataPoints
      .filter((dp) => dp?.title && dp?.content)
      .map((dp) => [
        `**${dp.title}**`,
        dp.content,
        dp.source ? `[Source](${dp.source})` : '',
      ].filter(Boolean).join('\n'))
      .join('\n\n');
  }, [dataPoints]);

  if (agents.length === 0) return null;

  return (
    <Box className={`${classes.wrapper} ${className || ''}`}>
      <Text size="xs" fw={700} c="dimmed" className={classes.title}>Show Thinking</Text>
      {agents.map((agent) => {
        const isExpanded = !effectiveCollapsedAgents.has(agent.key);
        const isFailed = agent.status === 'failed';
        const isActive = isStreaming && agent.status === 'running';
        const isDone = !isFailed && !isActive && (!isStreaming || agent.status === 'done');
        const retrievedDocuments = splitRetrievedDocuments(agent.content);
        const sourcesExpanded = expandedSources.has(agent.key);

        return (
          <div
            key={agent.key}
            className={`${classes.item} ${isExpanded ? classes.itemActive : ''}`}
          >
            <button
              type="button"
              className={classes.header}
              onClick={() => toggle(agent.key, isExpanded)}
              aria-expanded={isExpanded}
            >
              <IconRobot size={16} className={classes.agentIcon} />
              <span className={classes.agentName}>{agent.label}</span>
              {isActive && <span className={classes.streamingDot} data-testid="agent-running-indicator" aria-label={`${agent.label} running`} />}
              {isFailed && <IconAlertTriangle size={14} className={classes.doneIcon} data-testid="agent-failed-indicator" aria-label={`${agent.label} failed`} />}
              {isDone && <IconCheck size={14} className={classes.doneIcon} data-testid="agent-done-indicator" aria-label={`${agent.label} done`} />}
              <IconChevronDown
                size={16}
                className={`${classes.chevron} ${isExpanded ? classes.chevronExpanded : ''}`}
              />
            </button>
            <Collapse in={isExpanded}>
              <div className={classes.content}>
                <Text size="xs" fw={600} c={isFailed ? 'red' : 'dimmed'} mb={4}>{isFailed ? 'Failed' : 'Result'}</Text>
                {isFailed && agent.error && (
                  <Text size="sm" c="red" mb="xs">{agent.error}</Text>
                )}
                {isExpanded ? (
                  agent.content.trim() ? (
                    retrievedDocuments ? (
                      <>
                        {retrievedDocuments.leadContent && (
                          <AgentChunkContent content={retrievedDocuments.leadContent} isStreaming={isStreaming} />
                        )}
                        <div className={classes.sourceDocumentsPanel}>
                          <div className={classes.sourceDocumentsHeader}>
                            <div>
                              <Text size="sm" fw={600}>Source documents</Text>
                              <Text size="xs" c="dimmed">Retrieved chunks are hidden by default.</Text>
                            </div>
                            <Button
                              type="button"
                              size="xs"
                              variant={sourcesExpanded ? 'light' : 'subtle'}
                              leftSection={<IconFileText size={14} />}
                              className={classes.sourceDocumentsButton}
                              onClick={() => toggleSources(scopeId, agent.key)}
                              aria-expanded={sourcesExpanded}
                            >
                              {sourcesExpanded ? 'Hide source documents' : 'Show source documents'}
                            </Button>
                          </div>
                          {sourcesExpanded && (
                            <Collapse in={sourcesExpanded}>
                              <div className={classes.sourceDocumentsContent}>
                                <AgentChunkContent
                                  content={sourceDocumentsFromDataPoints || retrievedDocuments.sourceContent}
                                  isStreaming={isStreaming}
                                />
                              </div>
                            </Collapse>
                          )}
                        </div>
                      </>
                    ) : (
                      <AgentChunkContent content={agent.content} isStreaming={isStreaming} />
                    )
                  ) : (
                    <Text size="sm" c="dimmed">Waiting for result...</Text>
                  )
                ) : null}
              </div>
            </Collapse>
          </div>
        );
      })}
    </Box>
  );
};

export default AgentChunkAccordion;
